import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { scrollIntoViewSafe } from "@/lib/scroll";
import {
  PenLine, CheckCircle2, XCircle, Download, Loader2, Eraser, ShieldCheck, Users, Clock, ArrowDown, Send,
  Eye, CalendarClock, CheckSquare, Square,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCRIPT_FONT = "'Brush Script MT','Segoe Script','Dancing Script',cursive";
const fmtDate = (s) => {
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  try { return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); } catch { return s; }
};

// ─── fill-in-a-text-field sheet ───────────────────────────────────────────────
function TextFieldModal({ field, value, onSave, onClose }) {
  const [v, setV] = useState(value || "");
  const label = field.label || "Text";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="sign-text-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg text-slate-900">{label}{field.required ? "" : <span className="text-slate-400 font-normal text-sm"> (optional)</span>}</h3>
          <button data-testid="sign-text-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle className="h-6 w-6" /></button>
        </div>
        <input data-testid="sign-text-input" autoFocus value={v} maxLength={200} onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSave(v.trim())}
          placeholder={`Type your ${label.toLowerCase()}`}
          className="w-full rounded-xl border border-slate-200 px-3 py-3 text-base outline-none focus:border-slate-500" />
        <button data-testid="sign-text-save" onClick={() => onSave(v.trim())} disabled={field.required && !v.trim()}
          className="w-full rounded-full bg-emerald-600 text-white font-bold py-3.5 hover:bg-emerald-700 disabled:opacity-40">
          Save
        </button>
      </div>
    </div>
  );
}

// ─── draw-your-signature canvas ───────────────────────────────────────────────
function DrawPad({ onDirty, padRef }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  useEffect(() => {
    const c = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const w = c.offsetWidth, h = 170;
    c.width = w * dpr; c.height = h * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#1a2740";
    padRef.current = {
      clear: () => { ctx.clearRect(0, 0, w, h); padRef.current.dirty = false; onDirty(false); },
      toDataURL: () => c.toDataURL("image/png"),
      dirty: false,
    };
  }, [padRef, onDirty]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
    if (!padRef.current.dirty) { padRef.current.dirty = true; onDirty(true); }
  };
  const end = () => { drawing.current = false; };

  return (
    <canvas ref={canvasRef} data-testid="sign-draw-canvas"
      className="w-full h-[170px] rounded-xl border-2 border-dashed border-slate-300 bg-white cursor-crosshair"
      style={{ touchAction: "none" }}
      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
  );
}

// ─── adopt modal (type or draw + adopt consent) ───────────────────────────────
// Collects the signature (and initials) ONCE; the page then lets the signer place
// it on each field. For legacy requests without placed fields it signs directly.
function AdoptModal({ me, legacy, busy, onAdopt, onClose }) {
  const [mode, setMode] = useState("drawn");
  const [typed, setTyped] = useState(me.name || "");
  const [initials, setInitials] = useState(
    (me.name || "").split(" ").slice(0, 3).map((w) => w[0] || "").join("").toUpperCase());
  const [consent, setConsent] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const padRef = useRef({});

  const confirm = () => {
    if (!consent) { toast.error("Please check the box to adopt your signature."); return; }
    const data = { kind: mode };
    if (mode === "drawn") {
      if (!padRef.current.dirty) { toast.error("Draw your signature first."); return; }
      data.image = padRef.current.toDataURL();
      data.text = me.name;
    } else {
      if (!typed.trim()) { toast.error("Type your full name to sign."); return; }
      data.text = typed.trim();
    }
    if (me.needs_initials) data.initials = initials.trim();
    onAdopt(data);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="sign-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg text-slate-900">{legacy ? "Add your signature" : "Adopt your signature"}</h3>
          <button data-testid="sign-modal-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle className="h-6 w-6" /></button>
        </div>
        {!legacy && <p className="text-xs text-slate-500 -mt-2">You create it once — then tap each highlighted field on the document to place it.</p>}
        <div className="grid grid-cols-2 gap-1 rounded-full bg-slate-100 p-1 text-sm font-semibold">
          <button data-testid="sign-mode-draw" onClick={() => setMode("drawn")}
            className={`rounded-full py-2 ${mode === "drawn" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Draw</button>
          <button data-testid="sign-mode-type" onClick={() => setMode("typed")}
            className={`rounded-full py-2 ${mode !== "drawn" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Type</button>
        </div>
        {mode === "drawn" ? (
          <div className="space-y-2">
            <DrawPad onDirty={setDrawn} padRef={padRef} />
            <button data-testid="sign-draw-clear" onClick={() => padRef.current.clear?.()}
              className="text-xs font-semibold text-slate-500 hover:text-slate-800 flex items-center gap-1"><Eraser className="h-3.5 w-3.5" /> Clear</button>
          </div>
        ) : (
          <div className="space-y-2">
            <input data-testid="sign-typed-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type your full name"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500" />
            <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 h-[110px] flex items-center justify-center overflow-hidden">
              <span data-testid="sign-typed-preview" className="text-4xl text-slate-800 px-4 truncate" style={{ fontFamily: SCRIPT_FONT }}>
                {typed || "Your signature"}
              </span>
            </div>
          </div>
        )}
        {me.needs_initials && (
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-slate-600 shrink-0">Your initials<br /><span className="font-normal text-slate-400">(placed where marked)</span></label>
            <input data-testid="sign-initials-input" value={initials} maxLength={8}
              onChange={(e) => setInitials(e.target.value.toUpperCase())}
              className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-center font-bold tracking-widest outline-none focus:border-slate-500" />
          </div>
        )}
        <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
          <input data-testid="sign-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          <span>I agree that the signature I adopt here is the legal equivalent of my handwritten signature, and I consent to signing this document electronically.</span>
        </label>
        <button data-testid={legacy ? "sign-submit" : "sign-adopt-confirm"} onClick={confirm}
          disabled={busy || !consent || (mode === "drawn" ? !drawn : !typed.trim())}
          className="w-full rounded-full bg-emerald-600 text-white font-bold py-3.5 hover:bg-emerald-700 disabled:opacity-40 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
          {legacy ? "Sign document" : "Adopt & place"}
        </button>
      </div>
    </div>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────
export default function SignPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [adopted, setAdopted] = useState(null);   // {kind, text, image?, initials?} — created once
  const [applied, setApplied] = useState({});     // fieldKey -> true, filled field by field
  const [values, setValues] = useState({});       // fieldKey -> typed text / checkbox boolean
  const [textField, setTextField] = useState(null); // text field currently being filled in
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState("");
  const pendingField = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/sign/${token}`).then((r) => { setMeta(r.data); setErr(""); })
      .catch((e) => setErr(e.response?.data?.detail || "This signing link is invalid."));
  }, [token]);
  useEffect(load, [load]);

  const decline = async () => {
    try {
      await axios.post(`${API}/sign/${token}/decline`, { reason });
      setDeclineOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't record that"); }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div data-testid="sign-error" className="bg-white rounded-3xl border border-slate-200 shadow-sm p-10 text-center max-w-sm">
          <XCircle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="font-bold text-slate-800">{err}</p>
          <p className="text-sm text-slate-500 mt-2">If you think this is a mistake, reply to the text or email you received and the community team will help.</p>
        </div>
      </div>
    );
  }
  if (!meta) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>;
  }

  const me = meta.me || {};
  const isViewer = !!meta.viewer;
  const signed = me.status === "signed";
  const declined = me.status === "declined";
  const waiting = Boolean(meta.waiting_for) && !signed;
  const canSign = !isViewer && meta.status === "active" && me.status !== "signed" && !waiting;
  const fieldList = (me.fields || []).map((f, idx) => ({ ...f, _k: f.id || `f${idx}` }));
  const legacy = !isViewer && fieldList.length === 0; // requests created before placed fields existed
  const isFilled = (f) => f.kind === "text" ? !!(values[f._k] || "").trim()
    : f.kind === "checkbox" ? values[f._k] === true
    : !!applied[f._k];
  const isRequired = (f) => (f.kind === "text" || f.kind === "checkbox") ? !!f.required : true;
  const requiredList = fieldList.filter(isRequired);
  const appliedCount = requiredList.filter(isFilled).length;
  const allDone = !legacy && appliedCount === requiredList.length;
  const todayStr = new Date().toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  const FIELD_LABEL = { signature: "Sign here", initials: "Initials", date: "Date" };
  const labelFor = (f) => f.kind === "text" ? (f.label || "Text") : f.kind === "checkbox" ? (f.label || "") : FIELD_LABEL[f.kind];

  // Tap a field: dates stamp instantly; text opens a sheet; checkbox toggles; signature/initials adopt once, then place per tap.
  const tapField = (f) => {
    if (!canSign) return;
    if (f.kind === "text") { setTextField(f); return; }
    if (f.kind === "checkbox") { setValues((p) => ({ ...p, [f._k]: !p[f._k] })); return; }
    if (applied[f._k]) { setApplied((p) => { const n = { ...p }; delete n[f._k]; return n; }); return; }
    if (!adopted && f.kind !== "date") { pendingField.current = f._k; setAdoptOpen(true); return; }
    setApplied((p) => ({ ...p, [f._k]: true }));
  };
  const saveText = (v) => {
    setValues((p) => ({ ...p, [textField._k]: v }));
    setTextField(null);
  };
  const submit = async (data) => {
    const a = data || adopted;
    if (!a) { setAdoptOpen(true); return; }
    setBusy(true);
    try {
      const body = { consent: true, kind: a.kind, text: a.text, values: {} };
      if (a.kind === "drawn") body.image = a.image;
      if (me.needs_initials) body.initials = a.initials || "";
      fieldList.forEach((f) => {
        if (f.kind === "text") body.values[f.id] = (values[f._k] || "").trim();
        if (f.kind === "checkbox") body.values[f.id] = values[f._k] === true;
      });
      await axios.post(`${API}/sign/${token}`, body);
      toast.success("Signed — thank you!");
      setAdoptOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't record your signature"); }
    setBusy(false);
  };
  const onAdopt = (data) => {
    setAdopted(data);
    if (legacy) { submit(data); return; }
    setAdoptOpen(false);
    if (pendingField.current) {
      const k = pendingField.current;
      setApplied((p) => ({ ...p, [k]: true }));
      pendingField.current = null;
    }
  };
  const nextField = () => {
    const f = requiredList.find((x) => !isFilled(x)) || fieldList.find((x) => !isFilled(x));
    if (!f) return;
    scrollIntoViewSafe(document.querySelector(`[data-fieldkey="${f._k}"]`));
  };
  const savedValues = me.field_values || {};

  return (
    <div className="min-h-screen bg-slate-100 pb-28" data-testid="sign-page">
      {/* header */}
      <div className="bg-slate-900 text-white px-5 py-4 sticky top-0 z-30 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-emerald-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-bold truncate" data-testid="sign-title">{meta.title}</div>
            <div className="text-xs text-slate-300 truncate">{meta.community} · {isViewer ? `shared with ${me.name} (view only)` : `signature requested from ${me.name}`}</div>
          </div>
          <div className="text-xs font-bold bg-white/10 rounded-full px-3 py-1.5 shrink-0 flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> {meta.progress?.signed}/{meta.progress?.total} signed
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-5 space-y-4">
        {meta.message && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-sm text-amber-900" data-testid="sign-note">
            <span className="font-bold">Note from the requester: </span>{meta.message}
          </div>
        )}
        {isViewer && (
          <div data-testid="sign-viewer-banner" className="bg-white border border-slate-200 rounded-2xl px-4 py-3.5 flex items-center gap-3">
            <Eye className="h-6 w-6 text-slate-500 shrink-0" />
            <div className="text-sm text-slate-700 flex-1">
              <span className="font-bold">You&apos;re copied on this document — nothing to sign.</span>{" "}
              {meta.completed ? "Everyone has signed; the final copy is ready." : `We'll send you the final copy once all ${meta.progress?.total} signer${meta.progress?.total === 1 ? "" : "s"} have signed.`}
            </div>
            {meta.completed && (
              <a data-testid="sign-download" href={`${API}/sign/${token}/download`} target="_blank" rel="noreferrer"
                className="shrink-0 rounded-full bg-emerald-600 text-white text-xs font-bold px-4 py-2 hover:bg-emerald-700 flex items-center gap-1.5">
                <Download className="h-3.5 w-3.5" /> Signed copy
              </a>
            )}
          </div>
        )}
        {meta.expires_at && canSign && (
          <div data-testid="sign-deadline" className="bg-white border border-slate-200 rounded-2xl px-4 py-2.5 text-sm text-slate-700 flex items-center gap-2.5">
            <CalendarClock className="h-4 w-4 text-amber-500 shrink-0" />
            <span>Please sign by <span className="font-bold">{fmtDate(meta.expires_on || meta.expires_at)}</span> — the request voids after that.</span>
          </div>
        )}

        {waiting && (
          <div data-testid="sign-waiting-banner" className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3.5 flex items-center gap-3">
            <Clock className="h-6 w-6 text-blue-500 shrink-0" />
            <div className="text-sm text-blue-900">
              <span className="font-bold">This document is signed in order — it&apos;s not your turn yet.</span>{" "}
              Waiting for <span className="font-bold">{meta.waiting_for}</span> to sign first. We&apos;ll text/email you the moment it&apos;s your turn.
            </div>
          </div>
        )}
        {signed && (
          <div data-testid="sign-done-banner" className="bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3.5 flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
            <div className="text-sm">
              <span className="font-bold text-emerald-800">You signed this document.</span>{" "}
              <span className="text-emerald-700">
                {meta.completed ? "Everyone has signed — it's complete." : `Waiting on ${meta.progress.total - meta.progress.signed} more signer${meta.progress.total - meta.progress.signed === 1 ? "" : "s"}.`}
              </span>
            </div>
            {meta.completed && (
              <a data-testid="sign-download" href={`${API}/sign/${token}/download`} target="_blank" rel="noreferrer"
                className="ml-auto shrink-0 rounded-full bg-emerald-600 text-white text-xs font-bold px-4 py-2 hover:bg-emerald-700 flex items-center gap-1.5">
                <Download className="h-3.5 w-3.5" /> Signed copy
              </a>
            )}
          </div>
        )}
        {declined && (
          <div data-testid="sign-declined-banner" className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 text-sm text-slate-600">
            <span className="font-bold">You declined to sign.</span> The community team has been notified{meta.status === "active" ? " — changed your mind? You can still sign below." : ". If that changes, reply to the text or email you received and they can send a new request."}
          </div>
        )}
        {canSign && !legacy && (
          <div data-testid="sign-instructions" className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-600 flex items-center gap-2.5">
            <PenLine className="h-4 w-4 text-amber-500 shrink-0" />
            <span>Tap each highlighted field to fill it in — <span className="font-bold">{requiredList.length} required field{requiredList.length === 1 ? "" : "s"}</span>{fieldList.length > requiredList.length ? ` (+${fieldList.length - requiredList.length} optional)` : ""} — then press <span className="font-bold">Submit</span> below.</span>
          </div>
        )}

        {/* document pages */}
        {Array.from({ length: meta.page_count || 1 }, (_, i) => i + 1).map((n) => (
          <div key={n} className="relative bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <img src={`${API}/sign/${token}/page/${n}`} alt={`Page ${n}`} className="w-full" loading={n > 2 ? "lazy" : "eager"} />
            {fieldList.filter((f) => f.page === n).map((f) => {
              const filled = isFilled(f);
              const isBox = f.kind === "checkbox";
              const savedVal = savedValues[f.id];
              const inactive = !canSign && !signed; // declined / expired / canceled / waiting your turn — nothing to tap
              return (
                <button key={f._k} data-fieldkey={f._k}
                  data-testid={f.kind === "signature" ? "sign-here-box" : `sign-field-${f.kind}`}
                  onClick={() => tapField(f)} disabled={!canSign}
                  title={f.kind === "text" ? (f.label || "Text") : isBox ? (f.label || "Checkbox") : filled ? "Tap to remove" : undefined}
                  className={`absolute ${isBox ? "rounded-sm" : "rounded-md"} border-2 flex items-center ${isBox ? "justify-center" : "justify-center gap-1"} text-[11px] font-bold overflow-hidden transition-colors ${
                    signed ? "border-emerald-500 bg-emerald-100/60 text-emerald-700"
                    : inactive ? "border-slate-300 bg-slate-100/70 text-slate-400"
                    : filled ? "border-emerald-500 bg-white"
                    : f.kind === "signature" ? "border-amber-500 bg-amber-200/70 text-amber-900 animate-pulse hover:scale-[1.02]"
                    : (!isRequired(f)) ? "border-slate-300 border-dashed bg-slate-50/80 text-slate-500"
                    : "border-amber-400 bg-amber-100/60 text-amber-800"}`}
                  style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` }}>
                  {signed
                    ? (f.kind === "text" ? <span className="truncate px-1 text-slate-800 font-semibold">{savedVal || "—"}</span>
                      : isBox ? (savedVal ? <CheckSquare className="h-full w-full p-0.5 text-slate-800" /> : <Square className="h-full w-full p-0.5 text-slate-400" />)
                      : f.kind === "signature" ? <span className="truncate px-1 text-base text-slate-800" style={{ fontFamily: SCRIPT_FONT }}>{me.signature_text || me.name}</span>
                      : f.kind === "initials" ? <span className="tracking-widest text-slate-800">{me.signature_initials || ""}</span>
                      : <span className="text-slate-800">{fmtDate(me.signed_at)}</span>)
                    : isBox
                      ? (values[f._k] ? <CheckSquare className="h-full w-full p-0.5 text-slate-800" /> : <Square className="h-full w-full p-0.5" />)
                    : f.kind === "text"
                      ? (filled ? <span className="truncate px-1 text-slate-800 font-semibold">{values[f._k]}</span> : <span className="truncate px-1">{f.label || "Text"}{f.required ? "" : " (optional)"}</span>)
                    : filled
                      ? (f.kind === "signature"
                          ? (adopted?.image
                              ? <img src={adopted.image} alt="Your signature" className="h-full w-full object-contain p-0.5" />
                              : <span className="truncate px-1 text-lg text-slate-800" style={{ fontFamily: SCRIPT_FONT }}>{adopted?.text}</span>)
                          : f.kind === "initials"
                            ? <span className="tracking-widest text-slate-800">{adopted?.initials}</span>
                            : <span className="text-slate-800">{todayStr}</span>)
                      : <>{f.kind === "signature" && <PenLine className="h-3.5 w-3.5" />}<span className="truncate">{labelFor(f)}</span></>}
                </button>
              );
            })}
            {/* Other signers' completed fields — shown to viewers and once everything is signed */}
            {(meta.filled || []).map((s, si) => (s.fields || []).filter((f) => f.page === n).map((f, fi) => {
              const v = (s.field_values || {})[f.id];
              return (
                <div key={`filled-${si}-${fi}`} data-testid={`sign-filled-${f.kind}`}
                  title={`${s.name}${f.label ? ` · ${f.label}` : ""}`}
                  className="absolute rounded-md border-2 border-emerald-500 bg-emerald-50/70 text-emerald-800 flex items-center justify-center text-[11px] font-bold overflow-hidden"
                  style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%` }}>
                  {f.kind === "signature" ? <span className="truncate px-1 text-base text-slate-800" style={{ fontFamily: SCRIPT_FONT }}>{s.signature_text || s.name}</span>
                    : f.kind === "initials" ? <span className="tracking-widest text-slate-800">{s.signature_initials || ""}</span>
                    : f.kind === "date" ? <span className="text-slate-800">{fmtDate(s.signed_at)}</span>
                    : f.kind === "text" ? <span className="truncate px-1 text-slate-800">{v || "—"}</span>
                    : (v ? <CheckSquare className="h-full w-full p-0.5 text-slate-800" /> : <Square className="h-full w-full p-0.5 text-slate-400" />)}
                </div>
              );
            }))}
            {/* checkbox labels sit beside their tiny boxes so everyone knows what was (or is being) ticked */}
            {[...fieldList.filter((f) => f.page === n && f.kind === "checkbox" && f.label && (canSign || signed)),
              ...(meta.filled || []).flatMap((s) => (s.fields || []).filter((f) => f.page === n && f.kind === "checkbox" && f.label).map((f) => ({ ...f, _k: `filled-${f.id}`, _ro: true })))].map((f) => (
              <span key={`${f._k}-lbl`} className={`absolute text-[10px] font-semibold rounded px-1 pointer-events-none ${signed || f._ro ? "text-emerald-900 bg-emerald-50/90" : "text-amber-900 bg-amber-50/90"}`}
                style={{ left: `${(f.x + f.w) * 100 + 0.5}%`, top: `${f.y * 100}%` }}>
                {f.label}{f.required && !signed && !f._ro ? " *" : ""}
              </span>
            ))}
            <div className="text-center text-[10px] text-slate-400 py-1.5 border-t border-slate-100">Page {n} of {meta.page_count}</div>
          </div>
        ))}

        {/* other signers */}
        {meta.progress?.others?.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Other signers</p>
            <div className="flex flex-wrap gap-2">
              {meta.progress.others.map((o, i) => (
                <span key={i} className={`text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1.5 ${o.status === "signed" ? "bg-emerald-50 text-emerald-700" : o.status === "declined" ? "bg-slate-100 text-slate-400" : "bg-amber-50 text-amber-700"}`}>
                  {o.status === "signed" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <PenLine className="h-3.5 w-3.5" />}{o.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* sticky action bar */}
      {canSign && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            {legacy ? (
              <button data-testid="sign-open-modal" onClick={() => setAdoptOpen(true)}
                className="flex-1 rounded-full bg-emerald-600 text-white font-bold py-3.5 hover:bg-emerald-700 flex items-center justify-center gap-2">
                <PenLine className="h-4 w-4" /> Review complete — sign now
              </button>
            ) : (
              <>
                <div data-testid="sign-progress" className="shrink-0 text-xs font-bold text-slate-500 tabular-nums">
                  <span className={allDone ? "text-emerald-600" : "text-slate-700"}>{appliedCount}/{requiredList.length}</span> fields
                </div>
                {allDone ? (
                  <button data-testid="sign-submit" onClick={() => submit()} disabled={busy}
                    className="flex-1 rounded-full bg-emerald-600 text-white font-bold py-3.5 hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit signed document
                  </button>
                ) : (
                  <button data-testid="sign-next-field" onClick={nextField}
                    className="flex-1 rounded-full bg-amber-500 text-white font-bold py-3.5 hover:bg-amber-600 flex items-center justify-center gap-2">
                    <ArrowDown className="h-4 w-4" /> {appliedCount === 0 ? "Start — go to first field" : "Next field"}
                  </button>
                )}
              </>
            )}
            <button data-testid="sign-decline-open" onClick={() => setDeclineOpen(true)}
              className="text-sm font-semibold text-slate-400 hover:text-slate-700 px-2">Decline</button>
          </div>
        </div>
      )}

      {adoptOpen && <AdoptModal me={me} legacy={legacy} busy={busy}
        onClose={() => { setAdoptOpen(false); pendingField.current = null; }} onAdopt={onAdopt} />}
      {textField && <TextFieldModal field={textField} value={values[textField._k]} onSave={saveText} onClose={() => setTextField(null)} />}

      {declineOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-6" data-testid="sign-decline-modal">
          <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl p-6 space-y-3">
            <h3 className="font-bold text-slate-900">Decline to sign?</h3>
            <p className="text-sm text-slate-500">The community team will be notified. You can add a short reason (optional).</p>
            <textarea data-testid="sign-decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400" placeholder="Reason (optional)" />
            <div className="flex gap-2">
              <button data-testid="sign-decline-confirm" onClick={decline}
                className="flex-1 rounded-full bg-slate-800 text-white font-semibold py-2.5 hover:bg-slate-900">Decline</button>
              <button onClick={() => setDeclineOpen(false)} className="flex-1 rounded-full border border-slate-200 font-semibold py-2.5 hover:bg-slate-50">Keep reviewing</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
